import React from 'react';
import MeetingItem from './MeetingItem';

const CalendarView = ({ meetings }) => {
  // Mapping from Swedish month names to month index (0-11)
  const swedishMonthToIndex = {
    'januari': 0,
    'februari': 1,
    'mars': 2,
    'april': 3,
    'maj': 4,
    'juni': 5,
    'juli': 6,
    'augusti': 7,
    'september': 8,
    'oktober': 9,
    'november': 10,
    'december': 11
  };

  const groupMeetingsByMonth = (meetings) => {
    const grouped = {};
    
    meetings.forEach(meeting => {
      const date = new Date(meeting.date);
      const monthKey = date.toLocaleDateString('sv-SE', { 
        year: 'numeric', 
        month: 'long' 
      });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {};
      }
      
      const dateKey = meeting.date;
      if (!grouped[monthKey][dateKey]) {
        grouped[monthKey][dateKey] = [];
      }
      
      grouped[monthKey][dateKey].push(meeting);
    });
    
    return grouped;
  };

  const generateCalendarDays = (year, month) => {
    try {
      // Validate input parameters
      if (typeof year !== 'number' || typeof month !== 'number' || 
          isNaN(year) || isNaN(month) || month < 0 || month > 11) {
        console.warn('Invalid year or month:', year, month);
        return [];
      }
      
      const firstDay = new Date(year, month, 1);
      if (isNaN(firstDay.getTime())) {
        console.warn('Invalid first day created:', year, month);
        return [];
      }
      
      // Start from Monday (if first day is not Monday)
      const dayOfWeek = firstDay.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : -(dayOfWeek - 1);
      
      // Calculate start date more safely
      const startDate = new Date(year, month, 1);
      startDate.setDate(startDate.getDate() + mondayOffset);
      
      const days = [];
      const current = new Date(startDate);
      
      // Generate 6 weeks (42 days) to ensure full calendar grid
      for (let i = 0; i < 42; i++) {
        if (isNaN(current.getTime())) {
          console.warn('Invalid date generated at iteration:', i);
          break;
        }
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      
      return days;
    } catch (error) {
      console.error('Error generating calendar days:', error);
      return [];
    }
  };

  const isCurrentMonth = (date, year, month) => {
    return date.getFullYear() === year && date.getMonth() === month;
  };

  const formatDateKey = (date) => {
    try {
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        console.warn('Invalid date object:', date);
        return '';
      }
      // Use local date instead of UTC to avoid timezone offset issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.warn('Error formatting date:', error);
      return '';
    }
  };

  const monthOrder = [
    'augusti 2025',
    'september 2025', 
    'oktober 2025',
    'november 2025',
    'december 2025'
  ];

  const groupedMeetings = groupMeetingsByMonth(meetings);
  const sortedMonths = monthOrder.filter(month => 
    Object.keys(groupedMeetings).some(key => 
      key.toLowerCase() === month.toLowerCase()
    )
  );

  const weekdays = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

  if (meetings.length === 0) {
    return (
      <div className="calendar-view-empty">
        <div className="empty-state">
          <h3>Inga möten hittades</h3>
          <p>Prova att ändra dina filter för att se fler möten.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-view">
      {sortedMonths.map(monthKey => {
        const actualKey = Object.keys(groupedMeetings).find(key => 
          key.toLowerCase() === monthKey.toLowerCase()
        );
        
        if (!actualKey) return null;
        
        const monthMeetings = groupedMeetings[actualKey];
        
        // Parse month and year from the key
        const [monthName, year] = actualKey.split(' ');
        const monthIndex = swedishMonthToIndex[monthName.toLowerCase()];
        const yearNum = parseInt(year);
        
        // Safety check for invalid month
        if (monthIndex === undefined || isNaN(yearNum)) {
          console.warn(`Invalid month/year: ${monthName} ${year}`);
          return null;
        }
        
        const calendarDays = generateCalendarDays(yearNum, monthIndex);
        
        // Skip rendering if calendar generation failed
        if (!calendarDays || calendarDays.length === 0) {
          console.warn(`Failed to generate calendar for ${actualKey}`);
          return null;
        }
        
        return (
          <div key={monthKey} className="calendar-month">
            <div className="calendar-header">
              <h2 className="calendar-title">
                {actualKey.charAt(0).toUpperCase() + actualKey.slice(1)}
              </h2>
            </div>
            
            <div className="calendar-grid">
              {/* Weekday headers */}
              {weekdays.map(day => (
                <div key={day} className="calendar-weekday">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {calendarDays.map((date, index) => {
                const dateKey = formatDateKey(date);
                if (!dateKey) return null; // Skip invalid dates
                
                const dayMeetings = monthMeetings[dateKey] || [];
                const isThisMonth = isCurrentMonth(date, yearNum, monthIndex);
                
                let isToday = false;
                try {
                  isToday = date.toDateString() === new Date().toDateString();
                } catch (error) {
                  console.warn('Error checking if date is today:', error);
                }
                
                // Check for special meeting types on this day
                const hasSamainMeeting = dayMeetings.some(m => m.lodge === 'Samain');
                const hasStorlogeMeeting = dayMeetings.some(m => m.lodge?.includes('Storloge'));
                const hasIgldMeeting = dayMeetings.some(m => 
                  m.lodge?.includes('RiksStorLogen') || m.details?.includes('IGLD')
                );
                const hasJarnbaralandMeeting = dayMeetings.some(m => m.region === 'Järnbäraland');
                const hasFolkungalandMeeting = dayMeetings.some(m => m.region === 'Folkungaland');
                
                const dayClasses = [
                  'calendar-day',
                  !isThisMonth ? 'other-month' : '',
                  isToday ? 'today' : '',
                  dayMeetings.length > 0 ? 'has-meetings' : '',
                  hasSamainMeeting ? 'has-samain' : '',
                  hasStorlogeMeeting ? 'has-storloge' : '',
                  hasIgldMeeting ? 'has-igld' : '',
                  hasJarnbaralandMeeting ? 'has-jarnbaraland' : '',
                  hasFolkungalandMeeting ? 'has-folkungaland' : '',
                  dayMeetings.length > 2 ? 'many-meetings' : ''
                ].filter(Boolean).join(' ');
                
                return (
                  <div 
                    key={index} 
                    className={dayClasses}
                  >
                    <div className="day-number">
                      {date instanceof Date && !isNaN(date.getTime()) ? date.getDate() : '?'}
                    </div>
                    
                    {dayMeetings.length > 0 && (
                      <div className="day-meetings">
                        {dayMeetings.map((meeting, meetingIndex) => (
                          <MeetingItem 
                            key={meeting.id || `${dateKey}-${meeting.lodge}-${meeting.region || 'bardaland'}-${meetingIndex}`} 
                            meeting={meeting} 
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CalendarView;